import { EThemeKey } from '@constants';
import { App as AntdApp, ConfigProvider, ThemeConfig } from 'antd';
import esES from 'antd/locale/es_ES';
import dayjs from 'dayjs';
import dayLocaleData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DefaultTheme, ThemeProvider as Provider } from 'styled-components';

import { defaultTheme } from './theme';
import { THEMES } from './theme.config';

import 'dayjs/locale/es';

dayjs.extend(dayLocaleData);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

interface ThemeControlContextValue {
  theme: DefaultTheme;
  setThemeKey: (key: EThemeKey) => void;
}

const ThemeControlContext = createContext<ThemeControlContextValue | undefined>(undefined);

const buildAntdThemeConfig = (theme: DefaultTheme): ThemeConfig => ({
  token: {
    fontFamily: theme.fontFamily.primary,
    colorPrimary: theme.colors.black9,
    colorSuccess: theme.colors.success,
    colorError: theme.colors.red6,
    colorWarning: theme.colors.orange6,
    colorInfo: theme.colors.link,
    colorBgLayout: theme.colors.white,
    purple: theme.colors.purple6,
    green: theme.colors.green6,
    red: theme.colors.red6,
    orange: theme.colors.orange6
  },
  components: {
    Alert: {
      colorInfoBg: theme.colors.purple3
    },
    Modal: {
      colorPrimary: theme.colors.black9
    },
    Button: {
      fontFamily: theme.fontFamily.primary,
      borderRadius: 100,
      borderRadiusXS: 100,
      borderRadiusSM: 100,
      borderRadiusLG: 100
    },
    Segmented: {
      trackBg: theme.colors.black2
    },
    Tag: {
      defaultBg: theme.colors.black2,
      colorBorder: theme.colors.black4,
      colorText: theme.colors.black9,
      colorError: theme.colors.red6,
      colorErrorBg: theme.colors.red2,
      colorErrorBorder: theme.colors.red6,
      colorSuccess: theme.colors.green6,
      colorSuccessBg: theme.colors.green1,
      colorSuccessBorder: theme.colors.green6,
      colorWarning: theme.colors.orange7,
      colorWarningBg: theme.colors.orange2,
      colorWarningBorder: theme.colors.orange6
    },
    Input: {
      fontSize: 16,
      fontSizeSM: 14,
      fontSizeLG: 14,
      fontSizeXL: 14
    }
  }
});

interface BaseThemeProviderProps {
  children: React.ReactNode;
}

export const BaseThemeProvider: React.FC<BaseThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<DefaultTheme>(defaultTheme);

  const setThemeKey = useCallback((key: EThemeKey) => {
    const nextTheme = THEMES[key] || defaultTheme;
    setTheme(nextTheme);
  }, []);

  const themeControlContextValue = useMemo<ThemeControlContextValue>(
    () => ({ theme, setThemeKey }),
    [theme, setThemeKey]
  );

  const antdThemeConfig = useMemo(() => buildAntdThemeConfig(theme), [theme]);

  return (
    <ThemeControlContext.Provider value={themeControlContextValue}>
      <Provider theme={theme}>
        <ConfigProvider locale={esES} theme={antdThemeConfig}>
          <AntdApp component={false}>{children}</AntdApp>
        </ConfigProvider>
      </Provider>
    </ThemeControlContext.Provider>
  );
};

export const useThemeControl = (): ThemeControlContextValue => {
  const themeControlContext = useContext(ThemeControlContext);
  if (!themeControlContext)
    throw new Error('useThemeControl must be used within BaseThemeProvider');
  return themeControlContext;
};
